import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Trophy, Tag, CheckCircle2, XCircle, Lock } from "lucide-react";
import { api, API } from "@/lib/api";
import GameWindowBanner from "@/components/GameWindowBanner";

export default function TriviaPage() {
  const navigate = useNavigate();
  const token = useToken();
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [period, setPeriod] = useState("daily");
  const [leaderboard, setLeaderboard] = useState([]);

  const refresh = () => api.get(`/trivia/today?token=${token}`).then((r) => setData(r.data));

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => {
    api.get(`/trivia/leaderboard?token=${token}&period=${period}`).then((r) => setLeaderboard(r.data)).catch(() => {});
  }, [period, token, data]);

  const submit = async () => {
    if (pick === null || !data?.question?.id) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/trivia/submit?token=${token}`, { question_id: data.question.id, answer_index: pick });
      toast.success(r.data.correct ? `Correct! +${r.data.points}` : "Not quite — better luck tomorrow");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const q = data?.question;
  const locked = data?.submitted;
  const correctIdx = locked ? data.correct_index : null;

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex items-center justify-between themed-border" style={{ background: "var(--bq-header-bg)" }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="brutal-btn inline-flex items-center gap-1">
          <ArrowLeft size={14} /> HUB
        </button>
        <h1 className="font-black uppercase tracking-tight text-lg themed flex items-center gap-2">
          <Sparkles size={20} /> Trivia of the Day
        </h1>
        <div style={{ width: 60 }} />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 grid gap-6">
        <GameWindowBanner game="trivia" />
        {!q ? (
          <div data-testid="trivia-empty" className="brutal-card p-8 text-center">
            <Tag size={28} className="mx-auto mb-2" />
            <div className="font-black uppercase text-xl">No trivia scheduled today</div>
            <p className="text-sm mt-2" style={{ color: "var(--bq-muted)" }}>Check back tomorrow — or pester an admin to set one.</p>
          </div>
        ) : (
          <section data-testid="trivia-card" className="brutal-card p-6">
            {q.category && (
              <div className="inline-flex items-center gap-1 border-2 themed-border px-2 py-0.5 text-xs uppercase font-mono-bq mb-3" style={{ background: "var(--bq-accent2)" }}>
                <Tag size={12} /> {q.category}
              </div>
            )}
            <h2 data-testid="trivia-question" className="text-2xl md:text-3xl font-black uppercase mb-5">{q.question}</h2>

            <div className="grid gap-3">
              {q.options.map((opt, i) => {
                const isPicked = pick === i;
                const isCorrect = correctIdx !== null && i === correctIdx;
                const isMyWrong = locked && data.answer_index === i && i !== correctIdx;
                const baseStyle = { background: "var(--bq-card-bg)" };
                let cls = "p-4 border-2 themed-border text-left font-bold transition-all";
                if (locked) {
                  if (isCorrect) cls += " themed-shadow";
                  if (isMyWrong) cls += " opacity-80";
                } else {
                  cls += isPicked ? " themed-shadow-accent" : " hover:translate-y-[-2px]";
                }
                const pillBg = locked && isCorrect ? "var(--bq-accent)" : (locked && isMyWrong ? "var(--bq-danger)" : (isPicked ? "var(--bq-accent2)" : "var(--bq-card-bg)"));
                return (
                  <button
                    key={i}
                    data-testid={`trivia-option-${i}`}
                    disabled={locked || submitting}
                    onClick={() => setPick(i)}
                    className={cls}
                    style={{ ...baseStyle, background: pillBg }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 border-2 themed-border inline-flex items-center justify-center font-black" style={{ background: "var(--bq-bg)" }}>{String.fromCharCode(65 + i)}</span>
                        <span>{opt}</span>
                      </div>
                      {locked && isCorrect && <CheckCircle2 size={20} />}
                      {locked && isMyWrong && <XCircle size={20} />}
                    </div>
                  </button>
                );
              })}
            </div>

            {!locked ? (
              <button
                data-testid="trivia-submit-btn"
                disabled={pick === null || submitting}
                onClick={submit}
                className="brutal-btn mt-5 w-full disabled:opacity-50"
                style={{ background: "var(--bq-accent)" }}
              >
                {submitting ? "Submitting…" : "Lock in answer"}
              </button>
            ) : (
              <div data-testid="trivia-result" className="mt-5 p-3 border-2 themed-border flex items-center gap-2 text-sm font-bold" style={{ background: "var(--bq-card-bg)" }}>
                <Lock size={16} />
                {data.correct ? `Correct! +${data.score} points.` : "Not this time — see the right answer above."}
              </div>
            )}
          </section>
        )}

        <section className="brutal-card p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black uppercase flex items-center gap-2"><Trophy size={18} /> Leaderboard</h3>
            <div className="flex gap-2">
              {["daily", "weekly", "monthly"].map((p) => (
                <button
                  key={p}
                  data-testid={`trivia-period-${p}`}
                  onClick={() => setPeriod(p)}
                  className={`brutal-btn text-xs ${period === p ? "themed-shadow-accent" : ""}`}
                >{p.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {leaderboard.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bq-muted)" }}>No scores yet for this period.</p>
          ) : (
            <ol className="grid gap-2">
              {leaderboard.map((row, i) => (
                <li key={row.employee_id || i} data-testid={`trivia-lb-${i}`} className="flex items-center gap-3 border-2 themed-border p-2" style={{ background: "var(--bq-card-bg)" }}>
                  <div className="w-8 h-8 border-2 themed-border inline-flex items-center justify-center font-black">{i + 1}</div>
                  {row.avatar_path ? (
                    <img src={`${API}/files/${row.avatar_path}`} alt="" className="w-8 h-8 object-cover border-2 themed-border" />
                  ) : null}
                  <div className="flex-1 min-w-0 truncate font-bold">{row.name}</div>
                  <div className="font-mono-bq">{row.score} pts · {row.wins} ✓</div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}
